import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoRefreshOutline } from 'react-icons/io5';
import './AppLoadingScreen.css';

const AppLoadingScreen = ({ isInitializing, onRetry }) => {
  const { t } = useTranslation();
  const [showSlow, setShowSlow] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [minDurationMet, setMinDurationMet] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  // Minimum display time of 600ms to prevent flickering
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinDurationMet(true);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  // Animated dots for loading text
  useEffect(() => {
    if (!isInitializing || showSlow || showRetry) return;

    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);

    return () => clearInterval(interval);
  }, [isInitializing, showSlow, showRetry]);

  // Show slow message after 5s, retry after 10s
  useEffect(() => {
    if (!isInitializing) {
      setShowSlow(false);
      setShowRetry(false);
      return;
    }

    const slowTimer = setTimeout(() => {
      setShowSlow(true);
    }, 5000);

    const retryTimer = setTimeout(() => {
      setShowRetry(true);
    }, 10000);

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(retryTimer);
    };
  }, [isInitializing]);

  const handleRetry = () => {
    setShowSlow(false);
    setShowRetry(false);
    if (onRetry) {
      onRetry();
    }
  };

  // Get dots string for animation
  const getDots = () => '.'.repeat(dotCount);

  // Don't hide until minimum duration is met
  if (!isInitializing && !minDurationMet) {
    return (
      <div className="app-loading-screen">
        <img src="/header-icon.png" alt="emoozi" className="app-loading-logo-img" />
      </div>
    );
  }

  // Hide when initialization is complete
  if (!isInitializing) {
    return null;
  }

  return (
    <div className="app-loading-screen">
      <div className="app-loading-content">
        <img src="/header-icon.png" alt="emoozi" className="app-loading-logo-img" />

        <div className="app-loading-message">
          {showRetry ? (
            <>
              <span className="app-loading-error">{t('common.loadDataError')}</span>
              <button className="app-loading-retry" onClick={handleRetry}>
                <IoRefreshOutline />
                <span>{t('chat.retry')}</span>
              </button>
            </>
          ) : showSlow ? (
            <span className="app-loading-slow">{t('common.serverSlow')}</span>
          ) : (
            <span className="app-loading-text">
              {t('common.connectingToServer')}
              <span className="app-loading-dots">{getDots()}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppLoadingScreen;
